import { useState } from 'react';
import { usePDIContext } from '../context/PDIContext';
import {
  Gauge,
  AlertTriangle,
  CheckCircle2,
  AlertOctagon,
  Info,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';

const OdometerChecker = () => {
  const { carProfile, updateCarProfile } = usePDIContext();
  const [deliveryOdo, setDeliveryOdo] = useState(carProfile?.deliveryOdo || '');
  const [serviceYardOdo, setServiceYardOdo] = useState(carProfile?.serviceYardOdo || '');
  const [showEducation, setShowEducation] = useState(false);

  const handleSave = () => {
    updateCarProfile({
      ...carProfile,
      deliveryOdo: deliveryOdo ? parseInt(deliveryOdo) : null,
      serviceYardOdo: serviceYardOdo ? parseInt(serviceYardOdo) : null,
    });
  };

  const analyzeOdometer = () => {
    const delivery = parseInt(deliveryOdo) || 0;
    const serviceYard = parseInt(serviceYardOdo) || 0;
    
    const warnings = [];
    let status = 'ok'; // ok, caution, warning, danger

    // Check delivery odometer
    if (delivery > 100) {
      warnings.push({
        type: 'high_delivery',
        message: `Delivery odometer (${delivery} km) is higher than expected`,
        severity: 'warning',
        detail: 'New cars typically have under 50 km. Over 100 km is suspicious.',
      });
      status = 'warning';
    } else if (delivery > 50) {
      warnings.push({
        type: 'moderate_delivery',
        message: `Delivery odometer (${delivery} km) is slightly high`,
        severity: 'caution',
        detail: 'May indicate test drives. Ask for explanation.',
      });
      if (status === 'ok') status = 'caution';
    }

    // Check service yard reading if provided
    if (serviceYardOdo && deliveryOdo) {
      const gap = delivery - serviceYard;
      
      if (gap > 20) {
        warnings.push({
          type: 'large_gap',
          message: `${gap} km added since service yard (${serviceYard} → ${delivery})`,
          severity: 'caution',
          detail: 'Some movement for preparation is normal, but >20 km warrants explanation.',
        });
        if (status === 'ok') status = 'caution';
      }
      
      if (gap < 0) {
        warnings.push({
          type: 'odometer_rollback',
          message: 'Odometer appears to have decreased - possible tampering',
          severity: 'danger',
          detail: 'This is a serious red flag. Demand explanation immediately.',
        });
        status = 'danger';
      }
    }

    // Check if delivery odo was on invoice vs actual
    if (delivery === 0) {
      return { status: 'ok', warnings: [], message: 'Enter odometer reading to analyze' };
    }

    if (warnings.length === 0) {
      return { 
        status: 'ok', 
        warnings: [], 
        message: `${delivery} km is within acceptable range for a new car` 
      };
    }

    return { status, warnings };
  };

  const analysis = analyzeOdometer();

  const statusColors = {
    ok: 'brand',
    caution: 'accent',
    warning: 'accent',
    danger: 'danger',
  };

  const StatusIcon = {
    ok: CheckCircle2,
    caution: AlertTriangle,
    warning: AlertTriangle,
    danger: AlertOctagon,
  }[analysis.status];

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 bg-gradient-to-r from-slate-800 to-slate-900 text-white">
        <div className="flex items-center gap-2">
          <Gauge className="w-5 h-5 text-brand-400" />
          <h3 className="font-semibold">Odometer Fraud Check</h3>
        </div>
        <p className="text-xs text-slate-300 mt-1">
          Verify odometer readings match expectations
        </p>
      </div>

      <div className="p-4 space-y-4">
        {/* Inputs */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Delivery Odometer
            </label>
            <div className="relative">
              <input
                type="number"
                value={deliveryOdo}
                onChange={(e) => setDeliveryOdo(e.target.value)}
                onBlur={handleSave}
                placeholder="e.g., 25"
                className="pr-10"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">
                km
              </span>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Service Yard Odo
              <span className="text-slate-400 font-normal"> (optional)</span>
            </label>
            <div className="relative">
              <input
                type="number"
                value={serviceYardOdo}
                onChange={(e) => setServiceYardOdo(e.target.value)}
                onBlur={handleSave}
                placeholder="If known"
                className="pr-10"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">
                km
              </span>
            </div>
          </div>
        </div>

        {/* Analysis Result */}
        {deliveryOdo && (
          <div className={`p-4 rounded-xl border-2 ${
            analysis.status === 'ok' 
              ? 'bg-brand-50 border-brand-200'
              : analysis.status === 'danger'
              ? 'bg-danger-50 border-danger-300'
              : 'bg-accent-50 border-accent-200'
          }`}>
            <div className="flex items-start gap-3">
              <StatusIcon className={`w-5 h-5 flex-shrink-0 ${
                analysis.status === 'ok' 
                  ? 'text-brand-600'
                  : analysis.status === 'danger'
                  ? 'text-danger-600'
                  : 'text-accent-600'
              }`} />
              <div className="flex-1">
                {analysis.message ? (
                  <p className={`font-medium ${
                    analysis.status === 'ok' 
                      ? 'text-brand-800'
                      : analysis.status === 'danger'
                      ? 'text-danger-800'
                      : 'text-accent-800'
                  }`}>
                    {analysis.message}
                  </p>
                ) : (
                  <div className="space-y-3">
                    {analysis.warnings.map((warning, i) => (
                      <div key={i}>
                        <p className={`font-medium ${
                          warning.severity === 'danger' 
                            ? 'text-danger-800'
                            : 'text-accent-800'
                        }`}>
                          {warning.message}
                        </p>
                        <p className={`text-sm mt-0.5 ${
                          warning.severity === 'danger' 
                            ? 'text-danger-700'
                            : 'text-accent-700'
                        }`}>
                          {warning.detail}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Education Section */}
        <button
          onClick={() => setShowEducation(!showEducation)}
          className="w-full flex items-center justify-center gap-2 py-2 text-sm text-slate-600 hover:text-slate-800"
        >
          <Info className="w-4 h-4" />
          {showEducation ? 'Hide' : 'Learn about'} acceptable odometer ranges
          {showEducation ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>

        {showEducation && (
          <div className="p-4 bg-slate-50 rounded-xl space-y-3 animate-slide-down">
            <h4 className="font-medium text-slate-800">Acceptable Ranges for New Cars:</h4>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-3">
                <span className="w-20 text-slate-500">0-30 km</span>
                <span className="text-brand-600 font-medium">✓ Excellent</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="w-20 text-slate-500">30-50 km</span>
                <span className="text-brand-600 font-medium">✓ Good</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="w-20 text-slate-500">50-100 km</span>
                <span className="text-accent-600 font-medium">⚠ Ask why</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="w-20 text-slate-500">100+ km</span>
                <span className="text-danger-600 font-medium">❌ Suspicious</span>
              </div>
            </div>
            
            <div className="pt-3 border-t border-slate-200">
              <p className="text-xs text-slate-600">
                <strong>Common reasons for higher readings:</strong> Test drives, pre-delivery inspection by dealer, 
                transport (should be minimal), demo car usage (unacceptable for new car sale).
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OdometerChecker;
